import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as stylex from '@stylexjs/stylex';
import clsx from 'clsx';
import { getQTipData } from '../Tooltip/qTip.utilities';
import { colors } from '../theme/colors.stylex';
import type React from 'react';
import type { AllIconProps, ImportedSvgIconProps, LegacyIconProps } from './Icon.types';

const styles = stylex.create({
  base: { outlineStyle: 'none' },
  interactive: { cursor: 'pointer' },
});

const colorStyles = stylex.create({
  'theme': { color: colors.accent },
  'white': { color: colors.white },
  'dark-gray': { color: colors.textSubtle },
  'warning': { color: colors.warning },
  'darkish-gray': { color: colors.textMuted },
  'gray': { color: colors.iconMuted },
  'color': {},
  'info': { color: colors.link },
  'text': { color: colors.text },
  'inherit': {},
  'danger': { color: colors.borderInvalid },
  'theme-light': { color: colors.accentSubtle },
  'success': { color: colors.success },
});

/**
 * Icon:
 *  - access to Seeq custom icons by providing the desired icon
 *  - leverage "type" to style your icon
 */
/* oxlint-disable typescript/unified-signatures -- Separate overloads preserve renderer-specific JSX contextual typing. */
function Icon(props: LegacyIconProps): React.ReactElement;
function Icon(props: ImportedSvgIconProps): React.ReactElement;
function Icon(props: AllIconProps): React.ReactElement;
/* oxlint-enable typescript/unified-signatures */
function Icon({
  onClick,
  icon,
  iconPrefix,
  type = 'theme',
  extraClassNames,
  xstyle,
  id,
  large,
  small,
  size,
  color,
  testId,
  customId,
  number,
  ...tooltipProps
}: AllIconProps) {
  if ((type === 'color' && (color === undefined || color === '')) || (color && type !== 'color')) {
    const errorMessage =
      color === undefined || color === ''
        ? 'Icon with type="color" must have prop color specified.'
        : 'Icon with prop color must have type="color".';
    return <div {...stylex.props(colorStyles.danger)}>{errorMessage}</div>;
  }
  const styleProps = stylex.props(styles.base, colorStyles[type], onClick && styles.interactive, xstyle);
  const style = { ...styleProps.style, ...(type === 'color' && color ? { color } : {}) };
  const appliedSize = size ?? (small ? 'sm' : large ? 'lg' : undefined);
  const sharedClassNames = clsx(styleProps.className, extraClassNames);

  const tooltipData = getQTipData(tooltipProps);

  if (typeof icon !== 'string') {
    return (
      <FontAwesomeIcon
        icon={icon}
        size={appliedSize}
        widthAuto={true}
        className={sharedClassNames}
        style={style}
        onClick={onClick as React.MouseEventHandler<SVGSVGElement> | undefined}
        aria-label={onClick ? tooltipProps?.tooltip : undefined}
        data-testid={testId}
        data-icon-type={type}
        data-customid={customId}
        id={id}
        data-number={number}
        {...tooltipData}
      />
    );
  }

  const fontAwesomePrefix = iconPrefix ? iconPrefix : 'fa-sharp fa-regular';
  const iconPrefixString = icon.startsWith('fc') ? 'fc' : fontAwesomePrefix;
  const sizeClass = appliedSize ? `fa-${appliedSize}` : '';
  const appliedClassNames = `${iconPrefixString} ${icon} ${sizeClass} ${sharedClassNames}`;

  return (
    <i
      className={appliedClassNames}
      style={style}
      onClick={onClick as LegacyIconProps['onClick']}
      data-testid={testId}
      data-icon-type={type}
      aria-label={onClick ? tooltipProps?.tooltip : undefined}
      data-customid={customId}
      id={id}
      data-number={number}
      {...tooltipData}
    />
  );
}
export default Icon;
